// Таблица «Рекламы» в выбранном разрезе: кампании (дерево кампания → адсет → объявление), объявления списком,
// офферы, креативы (ролики пула), дни. Сверху — строка «Итого». Колонки выбирает владелец, сортировка — по клику
// на заголовок (на телефоне — в полосе фильтров). Цифры строк считает api, здесь только порядок и вид.
import { dayLabel, tashkentDay } from '@autoaff/schemas/period';
import { Button, IconButton, Popover, plural, Segmented, usd } from '@autoaff/ui';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronRight, Columns3 } from 'lucide-react';
import { Fragment, type ReactNode, useCallback, useRef, useState } from 'react';
import {
  type AdMetrics,
  type AdsData,
  type AdsView,
  type AdView,
  adStatus,
  type Budget,
  type CampaignView,
} from '../../business';
import { FilterSelect, SortTh } from '../../filters';
import {
  COLUMN,
  COLUMNS,
  type ColKey,
  defaultCols,
  phoneCols,
  type Sort,
  type SortKey,
  sortRows,
  tone,
} from './columns';

export const VIEWS: Array<{ value: AdsView; label: string }> = [
  { value: 'campaigns', label: 'Кампании' },
  { value: 'ads', label: 'Объявления' },
  { value: 'offers', label: 'Офферы' },
  { value: 'creatives', label: 'Креативы' },
  { value: 'days', label: 'Дни' },
];
export const VIEW_KEYS = VIEWS.map((v) => v.value);
/** Виды «Рекламы»: разрезы таблицы и «Разрезы» — аудитория Meta и апрув по разрезам лидов (Breakdowns.tsx). */
export type AdsMode = AdsView | 'breakdowns';
export const MODES: Array<{ value: AdsMode; label: string }> = [
  ...VIEWS,
  { value: 'breakdowns', label: 'Разрезы' },
];
export const MODE_KEYS = MODES.map((v) => v.value);

const NAME_HEAD: Record<AdsView, string> = {
  campaigns: 'Кампания · адсет · объявление',
  ads: 'Объявление',
  offers: 'Оффер',
  creatives: 'Ролик',
  days: 'День',
};

export function budgetText(b: Budget | null): string | null {
  if (!b || b.amount === null) return null;
  return b.kind === 'day' ? `${usd(b.amount, 0)} в день` : `${usd(b.amount, 0)} на весь срок`;
}

export function StatusText({ s }: { s: string | null }) {
  const st = adStatus(s);
  return (
    <span className={st.tone === 'danger' ? 'danger' : st.tone === 'warning' ? 'warn' : undefined}>
      {st.label}
    </span>
  );
}

interface TableProps {
  d: AdsData;
  view: AdsView;
  onView: (v: AdsMode) => void;
  cols: ColKey[];
  onCols: (cols: ColKey[]) => void;
  sort: Sort;
  onSort: (key: SortKey, firstDir?: 'asc' | 'desc') => void;
  /** Показать кампании оффера (клик по офферу в разрезе «Офферы»). */
  onOffer: (id: string) => void;
  /** «не моя»: кампания друга. */
  onForeign: (id: string, name: string) => void;
  /** Пока грузится новый выбор, видны прошлые строки — приглушённо. */
  busy: boolean;
  /** Строк нет: что показать вместо таблицы. */
  empty: ReactNode;
  /** Выбран вид «Разрезы» — вместо таблицы Breakdowns.tsx. */
  hidden?: boolean;
}

export function AdsTable(p: TableProps) {
  if (p.hidden) return null;
  const { d } = p;
  const rows = rowCount(d);
  return (
    <section
      className={clsx('card ads-table', p.busy && 'is-busy')}
      aria-busy={p.busy}
      aria-label="Таблица рекламы"
    >
      <header className="ads-table-head">
        <div className="hide-m">
          <Segmented<AdsMode> label="Разрез" value={p.view} onChange={p.onView} options={MODES} />
        </div>
        <div className="only-m">
          <FilterSelect<AdsMode>
            label="Разрез"
            value={p.view}
            onChange={p.onView}
            options={MODES}
            width={220}
            showValue
          />
        </div>
        <ColumnsMenu cols={p.cols} onChange={p.onCols} />
      </header>
      {rows === 0 ? p.empty : <Body {...p} />}
    </section>
  );
}

function rowCount(d: AdsData): number {
  switch (d.view) {
    case 'campaigns':
      return d.campaigns.length;
    case 'ads':
      return d.ads.length;
    case 'offers':
      return d.offers.length;
    case 'creatives':
      return d.creatives.length;
    default:
      return d.days.some((x) => x.spend > 0 || x.leads > 0) ? d.days.length : 0;
  }
}

function Body(p: TableProps) {
  const { d, cols, sort, onSort } = p;
  const phone = phoneCols(sort);
  return (
    <div className="tree-wrap">
      <table className={clsx('tbl tree', phone.length === 4 && 'm4')}>
        <thead>
          <tr>
            <SortTh k="name" sort={sort} onSort={onSort} firstDir={d.view === 'days' ? 'desc' : 'asc'}>
              {NAME_HEAD[d.view]}
            </SortTh>
            {cols.map((k) => (
              <SortTh
                key={k}
                k={k}
                sort={sort}
                onSort={onSort}
                firstDir={COLUMN[k].firstDir}
                className="num"
                title={COLUMN[k].hint}
              >
                {COLUMN[k].label}
              </SortTh>
            ))}
          </tr>
        </thead>
        <tbody>
          <MetricRow name="Итого" m={d.totals} cols={cols} phone={phone} total />
          {d.view === 'campaigns' && <Tree key={Object.values(d.filters).join('|')} {...p} phone={phone} />}
          {d.view === 'ads' &&
            sortRows(d.ads, sort).map((a) => (
              <MetricRow
                key={a.id}
                level={2}
                flat
                name={a.name}
                thumb={a.thumbnailUrl}
                sub={
                  <>
                    <AdSub a={a} />
                    {a.campaignName && (
                      <>
                        {' · '}
                        <span title={a.adsetName ? `${a.campaignName} › ${a.adsetName}` : a.campaignName}>
                          {a.campaignName}
                          {a.adsetName && ` › ${a.adsetName}`}
                        </span>
                      </>
                    )}
                  </>
                }
                m={a}
                cols={cols}
                phone={phone}
              />
            ))}
          {d.view === 'offers' &&
            sortRows(d.offers, sort).map((o) => (
              <MetricRow
                key={o.id}
                flat
                name={
                  <button
                    type="button"
                    className="ads-drill"
                    title="Показать кампании этого оффера"
                    onClick={() => p.onOffer(o.id)}
                  >
                    {o.name}
                  </button>
                }
                sub={
                  o.id === 'none'
                    ? 'объявления без ролика из пула и лиды без оффера'
                    : o.ads > 0
                      ? countAds(o.ads)
                      : 'объявлений в периоде нет'
                }
                m={o}
                cols={cols}
                phone={phone}
              />
            ))}
          {d.view === 'creatives' &&
            sortRows(d.creatives, sort).map((c) => (
              <MetricRow
                key={c.id}
                level={2}
                flat
                thumb={c.thumbnailUrl}
                name={
                  c.id === 'none' ? (
                    c.name
                  ) : (
                    <Link to="/creatives/$id" params={{ id: c.id }} className="ads-drill mono">
                      {c.name}
                    </Link>
                  )
                }
                sub={
                  c.id === 'none'
                    ? 'объявления, не связанные с роликом'
                    : [
                        c.title,
                        c.offer,
                        c.ads > 0 ? countAds(c.ads) : null,
                        // Динамическое объявление: лиды и апрувы поделены между роликами по лидам Meta
                        c.estimated ? 'лиды — оценка по доле ролика' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                }
                m={c}
                cols={cols}
                phone={phone}
              />
            ))}
          {d.view === 'days' &&
            sortRows(
              d.days.map((x) => ({ ...x, name: x.day })),
              sort,
            ).map((x) => (
              <MetricRow
                key={x.day}
                flat
                name={dayLabel(x.day)}
                sub={weekday(x.day)}
                m={x}
                cols={cols}
                phone={phone}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
}

const WEEKDAYS = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
function weekday(day: string): string {
  const today = tashkentDay();
  if (day === today) return 'сегодня';
  const yesterday = new Date(Date.parse(`${today}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
  if (day === yesterday) return 'вчера';
  return WEEKDAYS[new Date(`${day}T00:00:00Z`).getUTCDay()] ?? '';
}

const countAds = (n: number) => `${n} ${plural(n, 'объявление', 'объявления', 'объявлений')}`;

/** Статус, ролик пула, ссылка без ID и причины отклонения — под названием объявления. */
function AdSub({ a }: { a: AdView }) {
  return (
    <>
      <StatusText s={a.effectiveStatus} />
      {a.creative && (
        <>
          {' · '}
          <Link
            to="/creatives/$id"
            params={{ id: String(a.creative.id) }}
            className="mono ads-drill"
            title={`Ролик оффера «${a.creative.offer}»`}
          >
            {a.creative.slug}_{a.creative.code}
          </Link>
        </>
      )}
      {!a.tagsAdId && <span className="warn"> · нет ID в ссылке</span>}
      {a.review && <span className="danger"> · {Object.keys(a.review).join(', ')}</span>}
    </>
  );
}

function Tree(p: TableProps & { phone: ColKey[] }) {
  const { d, sort, cols, phone } = p;
  // Одна кампания или поиск — сразу раскрыто: найденное объявление видно без кликов
  const [open, setOpen] = useState<Set<string>>(() => {
    if (d.filters.q) return new Set(d.campaigns.flatMap((c) => [c.id, ...c.adsets.map((s) => s.id)]));
    return new Set(d.campaigns.length === 1 ? d.campaigns.map((c) => c.id) : []);
  });
  const flip = useCallback(
    (id: string) =>
      setOpen((s) => {
        const n = new Set(s);
        if (n.has(id)) n.delete(id);
        else n.add(id);
        return n;
      }),
    [],
  );
  return (
    <>
      {sortRows(d.campaigns, sort).map((c) => (
        <Fragment key={c.id}>
          <MetricRow
            level={0}
            name={c.name}
            flip={{ open: open.has(c.id), kids: c.adsets.length, onFlip: () => flip(c.id) }}
            sub={<CampaignSub c={c} onForeign={p.onForeign} />}
            m={c}
            cols={cols}
            phone={phone}
          />
          {open.has(c.id) &&
            sortRows(c.adsets, sort).map((s) => (
              <Fragment key={s.id}>
                <MetricRow
                  level={1}
                  name={s.name}
                  flip={{ open: open.has(s.id), kids: s.ads.length, onFlip: () => flip(s.id) }}
                  sub={
                    <>
                      <StatusText s={s.effectiveStatus} />
                      {budgetText(s.budget) && ` · ${budgetText(s.budget)}`}
                      {s.learning === 'LEARNING' && ' · обучение'}
                      {s.learning === 'FAIL' && ' · обучение не завершено'}
                    </>
                  }
                  m={s}
                  cols={cols}
                  phone={phone}
                />
                {open.has(s.id) &&
                  sortRows(s.ads, sort).map((a) => (
                    <MetricRow
                      key={a.id}
                      level={2}
                      name={a.name}
                      thumb={a.thumbnailUrl}
                      sub={<AdSub a={a} />}
                      m={a}
                      cols={cols}
                      phone={phone}
                    />
                  ))}
              </Fragment>
            ))}
        </Fragment>
      ))}
    </>
  );
}

function CampaignSub({ c, onForeign }: { c: CampaignView; onForeign: (id: string, name: string) => void }) {
  const b = budgetText(c.budget);
  const issue = c.issues?.[0]?.summary;
  return (
    <>
      <StatusText s={c.effectiveStatus} />
      {b && ` · ${b}`}
      {` · адсетов ${c.adsets.length}`}
      {issue && <span className="warn"> · {issue}</span>}
      {' · '}
      <button
        type="button"
        className="tree-mark"
        title="Кампания друга: не учитывать её расход в итогах, бюджете и страховке"
        onClick={() => onForeign(c.id, c.name)}
      >
        не моя
      </button>
    </>
  );
}

function MetricRow({
  name,
  sub,
  m,
  cols,
  phone,
  level = 0,
  flat,
  flip,
  thumb,
  total,
}: {
  name: ReactNode;
  sub?: ReactNode;
  m: AdMetrics;
  cols: ColKey[];
  /** Какие цифры видны на телефоне. */
  phone: ColKey[];
  level?: 0 | 1 | 2;
  /** Без отступа уровня (объявления и ролики списком). */
  flat?: boolean;
  flip?: { open: boolean; kids: number; onFlip: () => void };
  thumb?: string | null;
  total?: boolean;
}) {
  const label = typeof name === 'string' ? name : undefined;
  return (
    <tr data-level={flat ? 'flat' : level} className={total ? 'tree-total' : undefined}>
      <td className="tree-name">
        <div className="tree-cell">
          {flip && flip.kids > 0 ? (
            <IconButton
              label={flip.open ? `Свернуть «${label}»` : `Развернуть «${label}»`}
              className="tree-flip"
              aria-expanded={flip.open}
              onClick={flip.onFlip}
            >
              <ChevronRight />
            </IconButton>
          ) : (
            !total && !flat && <span className="tree-gap" />
          )}
          {thumb !== undefined &&
            (thumb ? (
              <img className="tree-thumb" src={thumb} alt="" loading="lazy" />
            ) : (
              <span className="tree-thumb" />
            ))}
          <div className="min-w-0">
            <b>{name}</b>
            {sub && <span className="sub">{sub}</span>}
          </div>
        </div>
      </td>
      {cols.map((k) => {
        const c = COLUMN[k];
        const v = c.value(m);
        const zone = c.zone?.(m) ?? null;
        return (
          <td
            key={k}
            className={clsx('num', c.signed && v !== null && tone(v), zone?.tone)}
            title={zone?.title}
            data-label={c.label}
            data-m={phone.includes(k) ? undefined : 'hide'}
          >
            {c.format(m)}
          </td>
        );
      })}
    </tr>
  );
}

/** «Колонки»: какие цифры показать в таблице. Выбор помнит браузер; «Расход» есть всегда. */
function ColumnsMenu({ cols, onChange }: { cols: ColKey[]; onChange: (cols: ColKey[]) => void }) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const changed = cols.join() !== defaultCols().join();
  return (
    <>
      <button
        ref={btn}
        type="button"
        className="btn filter-btn hide-m"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Columns3 aria-hidden />
        <span>Колонки</span>
      </button>
      <Popover anchor={btn} open={open} onClose={close} label="Колонки таблицы" align="end" width={320}>
        <fieldset className="cols-pick">
          <legend className="pick-sec">Какие цифры показать</legend>
          <div className="cols-grid">
            {COLUMNS.map((c) => (
              <label
                key={c.key}
                className="pick-opt cols-opt"
                title={c.key === 'spend' ? 'Расход виден всегда' : c.hint}
              >
                <input
                  type="checkbox"
                  checked={cols.includes(c.key)}
                  disabled={c.key === 'spend'}
                  onChange={(e) =>
                    onChange(e.target.checked ? [...cols, c.key] : cols.filter((k) => k !== c.key))
                  }
                />
                <span>{c.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
        {changed && (
          <div className="cols-foot">
            <Button size="sm" variant="ghost" onClick={() => onChange(defaultCols())}>
              Вернуть как было
            </Button>
          </div>
        )}
      </Popover>
    </>
  );
}
